import { getTier } from "./config";
import { hashNumbers } from "./random";

export type TokyoTerrainZone =
  | "HOME"
  | "GENKAN"
  | "ROAD"
  | "CURB"
  | "TERRACE";

export interface TerrainBounds {
  readonly minX: number;
  readonly maxX: number;
  readonly minZ: number;
  readonly maxZ: number;
}

/**
 * The first run starts on this zero-height floor. The open side faces -Z so
 * the initial camera and mission route can lead through the genkan to Tokyo.
 */
export const INITIAL_HOME_BOUNDS: TerrainBounds = {
  minX: -3.8,
  maxX: 3.8,
  minZ: -2.7,
  maxZ: 3.2,
};

export const INITIAL_GENKAN_BOUNDS: TerrainBounds = {
  minX: -1.15,
  maxX: 1.15,
  minZ: -4.15,
  maxZ: -2.55,
};

export const INITIAL_HOME_SPAWN_METERS = Object.freeze({ x: 0, z: 0 });
export const INITIAL_HOME_EXIT_METERS = Object.freeze({ x: 0, z: -4.4 });
export const INITIAL_GENKAN_DROP_METERS = 0.12;

export interface InitialHomeMovement {
  readonly xMeters: number;
  readonly zMeters: number;
  readonly exited: boolean;
  readonly blocked: boolean;
}

/**
 * Collision contract for the merged decorative home. The Three.js house is a
 * single low-cost mesh, so movement uses this matching 2D envelope instead of
 * per-wall rigid bodies. Before the tutorial is complete the front threshold
 * is closed; afterwards the only exit is the genkan corridor on the -Z side.
 */
export function constrainInitialHomeMovement(
  proposedXMeters: number,
  proposedZMeters: number,
  radiusMeters: number,
  doorUnlocked: boolean,
): InitialHomeMovement {
  const proposedX = Number.isFinite(proposedXMeters) ? proposedXMeters : 0;
  const proposedZ = Number.isFinite(proposedZMeters) ? proposedZMeters : 0;
  const safeRadius = Number.isFinite(radiusMeters) ? Math.max(0, radiusMeters) : 0;
  // The house bounds describe the wall centres plus their half thickness.
  // Keep the whole ball inside the visible mesh, not only its centre point.
  const clearance = Math.max(0.08, safeRadius + 0.12);
  const homeMinX = INITIAL_HOME_BOUNDS.minX + clearance;
  const homeMaxX = INITIAL_HOME_BOUNDS.maxX - clearance;
  const homeMaxZ = INITIAL_HOME_BOUNDS.maxZ - clearance;
  const frontWallZ = INITIAL_HOME_BOUNDS.minZ + clearance;
  const corridorHalfWidth = Math.max(0, INITIAL_GENKAN_BOUNDS.maxX - clearance);
  const corridorMinX = -corridorHalfWidth;
  const corridorMaxX = corridorHalfWidth;
  const clampValue = (value: number, minimum: number, maximum: number): number => (
    Math.max(minimum, Math.min(maximum, value))
  );

  if (proposedZ >= frontWallZ) {
    const xMeters = clampValue(proposedX, homeMinX, homeMaxX);
    const zMeters = Math.min(proposedZ, homeMaxZ);
    return {
      xMeters,
      zMeters,
      exited: false,
      blocked: xMeters !== proposedX || zMeters !== proposedZ,
    };
  }

  const insideDoorway = proposedX >= corridorMinX && proposedX <= corridorMaxX;
  if (!doorUnlocked || !insideDoorway) {
    return {
      xMeters: clampValue(proposedX, homeMinX, homeMaxX),
      zMeters: frontWallZ,
      exited: false,
      blocked: true,
    };
  }

  const xMeters = clampValue(proposedX, corridorMinX, corridorMaxX);
  return {
    xMeters,
    zMeters: proposedZ,
    exited: proposedZ <= INITIAL_HOME_EXIT_METERS.z + 0.15,
    blocked: xMeters !== proposedX,
  };
}

const boundedTierIndex = (tierIndex: number): number => (
  Number.isFinite(tierIndex) ? Math.max(0, Math.min(5, Math.floor(tierIndex))) : 0
);

const contains = (bounds: TerrainBounds, xMeters: number, zMeters: number): boolean => (
  xMeters >= bounds.minX
  && xMeters <= bounds.maxX
  && zMeters >= bounds.minZ
  && zMeters <= bounds.maxZ
);

const centeredModulo = (value: number, period: number): number => {
  const wrapped = ((value + period * 0.5) % period + period) % period;
  return wrapped - period * 0.5;
};

interface TerrainSample {
  readonly heightMeters: number;
  readonly zone: TokyoTerrainZone;
}

function sampleProceduralTokyo(
  xMeters: number,
  zMeters: number,
  tierIndex: number,
): TerrainSample {
  const tier = getTier(tierIndex);
  const unit = tier.metersPerUnit;
  const blockPeriod = unit * 12;
  const localX = Math.abs(centeredModulo(xMeters, blockPeriod));
  const localZ = Math.abs(centeredModulo(zMeters, blockPeriod));
  const roadHalfWidth = unit * 1.2;
  const curbOuterEdge = unit * 1.72;
  const blockX = Math.floor((xMeters + blockPeriod * 0.5) / blockPeriod);
  const blockZ = Math.floor((zMeters + blockPeriod * 0.5) / blockPeriod);
  const terraceLevel = hashNumbers(0x74_6f_6b_79, tierIndex, blockX, blockZ) % 3;
  const rollingBase = (
    Math.sin(xMeters / (blockPeriod * 2.8) + tierIndex * 0.41)
    + Math.cos(zMeters / (blockPeriod * 3.7) - tierIndex * 0.27)
  ) * unit * 0.014;

  if (localX <= roadHalfWidth || localZ <= roadHalfWidth) {
    return {
      heightMeters: rollingBase - unit * 0.04,
      zone: "ROAD",
    };
  }
  if (localX <= curbOuterEdge || localZ <= curbOuterEdge) {
    return {
      heightMeters: rollingBase + unit * 0.03,
      zone: "CURB",
    };
  }
  return {
    heightMeters: rollingBase + terraceLevel * unit * 0.035,
    zone: "TERRACE",
  };
}

function sampleTerrain(
  xMeters: number,
  zMeters: number,
  tierIndex: number,
): TerrainSample {
  const safeX = Number.isFinite(xMeters) ? xMeters : 0;
  const safeZ = Number.isFinite(zMeters) ? zMeters : 0;
  const tier = boundedTierIndex(tierIndex);

  if (tier === 0) {
    if (contains(INITIAL_GENKAN_BOUNDS, safeX, safeZ)) {
      return { heightMeters: -INITIAL_GENKAN_DROP_METERS, zone: "GENKAN" };
    }
    if (contains(INITIAL_HOME_BOUNDS, safeX, safeZ)) {
      return { heightMeters: 0, zone: "HOME" };
    }
  }

  return sampleProceduralTokyo(safeX, safeZ, tier);
}

/**
 * Deterministic physical ground height shared by rendering and future movement
 * collision. Heights stay small relative to each tier's metres-per-unit scale.
 */
export function terrainHeightMeters(
  xMeters: number,
  zMeters: number,
  tierIndex: number,
): number {
  return sampleTerrain(xMeters, zMeters, tierIndex).heightMeters;
}

export function terrainZoneAt(
  xMeters: number,
  zMeters: number,
  tierIndex: number,
): TokyoTerrainZone {
  return sampleTerrain(xMeters, zMeters, tierIndex).zone;
}

export function isInitialHomeInterior(xMeters: number, zMeters: number): boolean {
  return contains(INITIAL_HOME_BOUNDS, xMeters, zMeters);
}
